import { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';

import {
  FilterCheckboxItem,
  FilterItemNoResults,
  FilterSearch,
  FiltersList,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersDrawer/FiltersDrawer';
import IconTask from '@/icons/Task.svg';

import { useFilters } from '../FiltersState';
import { useRoleFilterState } from '../shared';

const MINIMUM_ROLES_FOR_SEARCH = 5;

export const FiltersRole = () => {
  const { t } = useLingui();

  const {
    roles: { roles, setRoles },
  } = useFilters();

  const {
    search,
    setSearch,
    fetchNextPage,
    isLoading,
    roles: roleOptions,
    count,
  } = useRoleFilterState();

  const selectedRoleIds = roles.map(({ id }) => id);

  const options: ReactNode[] = roleOptions.map((role) => (
    <FilterCheckboxItem key={role.id} id={role.id} label={role.name} />
  ));

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-mobile" />);
  }

  const toggleRole = (roleId: string) => {
    const role = roleOptions.find(({ id }) => id === roleId);

    if (role) {
      if (selectedRoleIds.includes(role.id)) {
        setRoles(roles.filter(({ id }) => id !== role.id));
      } else {
        setRoles([...roles, role]);
      }
    }
  };

  const optionsWithSearch = (
    <FilterSearch
      ariaLabel={t({
        id: 'settings.organization.members.invitations.filters.role.label',
        message: 'Role',
      })}
      inputProps={{
        ariaLabel: t({
          id: 'settings.organization.members.invitations.filters.role.search.label',
          message: 'Search roles',
        }),
        placeholder: t({
          id: 'settings.organization.members.invitations.filters.role.search.placeholder',
          message: 'Search roles...',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={roleOptions.length === 0 && isLoading}
      isLoadingMore={isLoading}
      values={selectedRoleIds}
      onChange={toggleRole}
    >
      {options}
    </FilterSearch>
  );

  const optionsWithoutSearch = (
    <FiltersList
      ariaLabel={t({
        id: 'settings.organization.members.invitations.filters.role.label',
        message: 'Role',
      })}
      values={selectedRoleIds}
      onChange={toggleRole}
    >
      {options}
    </FiltersList>
  );

  return (
    <FiltersSubMenu
      id="role"
      icon={<IconTask />}
      label={t({
        id: 'settings.organization.members.invitations.filters.role.label',
        message: 'Role',
      })}
      count={roles.length}
    >
      {count < MINIMUM_ROLES_FOR_SEARCH ? optionsWithoutSearch : optionsWithSearch}
    </FiltersSubMenu>
  );
};
